import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import { toast } from 'react-hot-toast';
import { FunnelIcon, PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/solid';
import RichTextEditor from '../common/RichTextEditor';
import { renderContentWithMentions } from '../../utils/mentions';

const ProjectNotes = ({ projectId }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visibility, setVisibility] = useState('all'); // 'all', 'internal', or 'external'
  const [showAddForm, setShowAddForm] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [isInternal, setIsInternal] = useState(true);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [phases, setPhases] = useState([]);
  const [selectedPhaseId, setSelectedPhaseId] = useState('');
  const [selectedStepId, setSelectedStepId] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [editingNote, setEditingNote] = useState(null);
  const [isUpdatingNote, setIsUpdatingNote] = useState(false);
  
  useEffect(() => {
    fetchNotes();
    fetchPhases();
    fetchCurrentUser();
  }, [projectId, visibility]);

  const fetchCurrentUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setCurrentUser(response.data.user);
    } catch (error) {
      console.error('Error fetching current user:', error);
    }
  };
  
  const fetchNotes = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get(`/projects/${projectId}/notes`, {
        params: { visibility }
      });
      
      if (response.data && response.data.notes) {
        setNotes(response.data.notes);
      } else {
        setNotes([]);
      }
    } catch (err) {
      console.error('Error fetching project notes:', err);
      setError('Failed to load project notes. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchPhases = async () => {
    try {
      const response = await api.get(`/projects/${projectId}`);
      
      if (response.data && response.data.project && response.data.project.phases) {
        setPhases(response.data.project.phases);
        
        // Set default selected phase and step if available
        if (response.data.project.phases.length > 0) {
          const firstPhase = response.data.project.phases[0];
          setSelectedPhaseId(firstPhase.id.toString());
          
          if (firstPhase.steps && firstPhase.steps.length > 0) {
            setSelectedStepId(firstPhase.steps[0].id.toString());
          }
        }
      }
    } catch (err) {
      console.error('Error fetching project phases:', err);
    }
  };
  
  const handlePhaseChange = (e) => {
    const phaseId = e.target.value;
    setSelectedPhaseId(phaseId);
    
    // Find the selected phase
    const selectedPhase = phases.find(phase => phase.id.toString() === phaseId.toString());
    
    // Reset step selection
    if (selectedPhase && selectedPhase.steps && selectedPhase.steps.length > 0) {
      setSelectedStepId(selectedPhase.steps[0].id.toString());
    } else {
      setSelectedStepId('');
    }
  };
  
  const handleAddNote = async (e) => {
    e.preventDefault();
    
    if (!newNote || !newNote.trim()) {
      toast.error('Note content cannot be empty');
      return;
    }
    
    if (!selectedStepId) {
      toast.error('Please select a step for this note');
      return;
    }
    
    try {
      setIsAddingNote(true);
      
      const response = await api.post(`/steps/${selectedStepId}/notes`, {
        content: newNote,
        is_internal: isInternal
      });
      
      if (response.data && response.data.note) {
        // Add step and phase information to the note
        const selectedPhase = phases.find(phase => phase.id.toString() === selectedPhaseId);
        const selectedStep = selectedPhase?.steps.find(step => step.id.toString() === selectedStepId);
        
        const newNoteWithDetails = {
          ...response.data.note,
          step_name: selectedStep?.name,
          phase_name: selectedPhase?.name,
          step_id: selectedStepId,
          phase_id: selectedPhaseId
        };
        
        // Add the new note to the list
        setNotes([newNoteWithDetails, ...notes]);
        
        // Reset form
        setNewNote('');
        setShowAddForm(false);
        
        toast.success('Note added successfully');
      }
    } catch (err) {
      console.error('Error adding note:', err);
      toast.error('Failed to add note. Please try again.');
    } finally {
      setIsAddingNote(false);
    }
  };

  const handleEditNote = (note) => {
    setEditingNote({
      ...note,
      editContent: note.content,
      editIsInternal: note.is_internal
    });
  };

  const handleUpdateNote = async (e) => {
    e.preventDefault();
    
    if (!editingNote.editContent || !editingNote.editContent.trim()) {
      toast.error('Note content cannot be empty');
      return;
    }
    
    try {
      setIsUpdatingNote(true);
      
      const response = await api.put(`/notes/${editingNote.id}`, {
        content: editingNote.editContent,
        is_internal: editingNote.editIsInternal
      });
      
      if (response.data) {
        // Update the note in the list
        setNotes(notes.map(note => 
          note.id === editingNote.id 
            ? { ...note, ...response.data, content: editingNote.editContent, is_internal: editingNote.editIsInternal }
            : note
        ));
        
        // Reset editing state
        setEditingNote(null);
        
        toast.success('Note updated successfully');
      }
    } catch (err) {
      console.error('Error updating note:', err);
      toast.error('Failed to update note. Please try again.');
    } finally {
      setIsUpdatingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note? This action cannot be undone.')) {
      return;
    }
    
    try {
      await api.delete(`/notes/${noteId}`);
      
      // Remove the note from the list
      setNotes(notes.filter(note => note.id !== noteId));
      
      toast.success('Note deleted successfully');
    } catch (err) {
      console.error('Error deleting note:', err);
      toast.error('Failed to delete note. Please try again.');
    }
  };

  const canUserEditNote = (note) => {
    return currentUser && note.created_by === currentUser.id;
  };
  
  // Helper function to format dates
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Get available steps for the selected phase
  const getAvailableSteps = () => {
    // Make sure we're comparing strings to strings for the ID
    const selectedPhase = phases.find(phase => phase.id.toString() === selectedPhaseId.toString());
    
    return selectedPhase?.steps || [];
  };

  if (loading && notes.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-48">
        <div className="w-10 h-10 border-t-2 border-b-2 border-gray-500 rounded-full animate-spin"></div>
        <p className="mt-4 text-gray-600">Loading notes...</p>
      </div>
    );
  }
  
  if (error && notes.length === 0) {
    return (
      <div className="p-4 text-red-700 bg-red-100 rounded-md">
        <p>{error}</p>
        <button 
          onClick={fetchNotes}
          className="mt-2 text-red-700 underline"
        >
          Retry
        </button>
      </div>
    );
  }
  
  return (
    <div>
      {/* Header with filters and add button */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Project Notes</h2>
        <div className="flex items-center space-x-4">
          <div className="flex items-center">
            <FunnelIcon className="h-5 w-5 text-gray-500 mr-2" />
            <select
              value={visibility}
              onChange={(e) => setVisibility(e.target.value)}
              className="pl-2 pr-8 py-1 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              <option value="all">All Notes</option>
              <option value="internal">Internal Notes Only</option>
              <option value="external">Client-Visible Notes Only</option>
            </select>
          </div>
          <button
            type="button"
            onClick={() => setShowAddForm(!showAddForm)}
            className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <PlusIcon className="h-4 w-4 mr-1" />
            Add Note
          </button>
        </div>
      </div>
      
      {/* Add Note Form */}
      {showAddForm && (
        <div className="bg-gray-50 p-4 rounded-md mb-6 border border-gray-200">
          <h3 className="text-base font-medium text-gray-900 mb-3">Add a New Note</h3>
          <form onSubmit={handleAddNote}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="phaseSelect" className="block text-sm font-medium text-gray-700 mb-1">
                  Phase
                </label>
                <select
                  id="phaseSelect"
                  value={selectedPhaseId}
                  onChange={handlePhaseChange}
                  className="w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  disabled={phases.length === 0}
                >
                  {phases.length === 0 ? (
                    <option value="">No phases available</option>
                  ) : (
                    phases.map(phase => (
                      <option key={phase.id} value={phase.id}>
                        {phase.name}
                      </option>
                    ))
                  )}
                </select>
              </div>
              <div>
                <label htmlFor="stepSelect" className="block text-sm font-medium text-gray-700 mb-1">
                  Step
                </label>
                <select
                  id="stepSelect"
                  value={selectedStepId}
                  onChange={(e) => setSelectedStepId(e.target.value)}
                  className="w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  disabled={getAvailableSteps().length === 0}
                >
                  {getAvailableSteps().length === 0 ? (
                    <option value="">No steps available</option>
                  ) : (
                    getAvailableSteps().map(step => (
                      <option key={step.id} value={step.id}>
                        {step.name}
                      </option>
                    ))
                  )}
                </select>
              </div>
            </div>
            <div className="mb-4">
              <RichTextEditor
                value={newNote}
                onChange={setNewNote}
                placeholder={`Add a ${isInternal ? 'internal' : 'client-visible'} note...`}
                maxLength={5000}
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="isInternal"
                  name="isInternal"
                  type="checkbox"
                  checked={isInternal}
                  onChange={(e) => setIsInternal(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="isInternal" className="ml-2 block text-sm text-gray-900">
                  Internal note (only visible to team)
                </label>
              </div>
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isAddingNote || !newNote || !selectedStepId}
                  className={`inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                    isAddingNote || !newNote || !selectedStepId ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                >
                  {isAddingNote ? 'Adding...' : 'Add Note'}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
      
      {/* Notes list */}
      {loading && (
        <div className="flex justify-center py-4">
          <div className="w-8 h-8 border-t-2 border-b-2 border-gray-500 rounded-full animate-spin"></div>
        </div>
      )}
      
      {notes.length > 0 ? (
        <div className="space-y-4">
          {notes.map((note) => (
            <div key={note.id} className="bg-white shadow overflow-hidden sm:rounded-md p-4">
              {editingNote && editingNote.id === note.id ? (
                // Edit Form
                <form onSubmit={handleUpdateNote} className="space-y-4">
                  <div>
                    <RichTextEditor
                      value={editingNote.editContent}
                      onChange={(content) => setEditingNote({...editingNote, editContent: content})}
                      placeholder="Edit note content..."
                      maxLength={5000}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <input
                        id={`editInternal-${note.id}`}
                        name={`editInternal-${note.id}`}
                        type="checkbox"
                        checked={editingNote.editIsInternal}
                        onChange={(e) => setEditingNote({...editingNote, editIsInternal: e.target.checked})}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor={`editInternal-${note.id}`} className="ml-2 block text-sm text-gray-900">
                        Internal note (only visible to team)
                      </label>
                    </div>
                    <div className="flex space-x-3">
                      <button
                        type="button"
                        onClick={() => setEditingNote(null)}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={isUpdatingNote || !editingNote.editContent}
                        className={`inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                          isUpdatingNote || !editingNote.editContent ? 'opacity-70 cursor-not-allowed' : ''
                        }`}
                      >
                        {isUpdatingNote ? 'Updating...' : 'Update Note'}
                      </button>
                    </div>
                  </div>
                </form>
              ) : (
                // Display Note
                <>
                  <div className="flex justify-between items-start">
                    <div className="flex-grow">
                      <div className="text-sm text-gray-900 prose prose-sm max-w-none rich-text-content">
                        {renderContentWithMentions(note.content)}
                      </div>
                      <div className="mt-2 text-xs text-gray-500">
                        Added by {note.created_by_name || (note.created_by ? 'Team Member' : 'Client')} on {formatDate(note.created_at)}
                        {note.updated_at && note.updated_at !== note.created_at && (
                          <span className="ml-2">(edited {formatDate(note.updated_at)})</span>
                        )}
                      </div>
                    </div>
                    <div className="ml-4 flex-shrink-0 flex items-center space-x-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        note.is_internal ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {note.is_internal ? 'Internal' : 'Client-Visible'}
                      </span>
                      {canUserEditNote(note) && (
                        <>
                          <button
                            onClick={() => handleEditNote(note)}
                            className="text-blue-600 hover:text-blue-900 text-xs"
                            title="Edit note"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteNote(note.id)}
                            className="text-red-600 hover:text-red-900 text-xs"
                            title="Delete note"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </>
                      )}
                      <Link
                        to={`/steps/${note.step_id}`}
                        className="text-blue-600 hover:text-blue-900 text-xs"
                      >
                        View Step
                      </Link>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center space-x-2 text-xs">
                    <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded font-medium">
                      {note.phase_name}
                    </span>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded font-medium">
                      {note.step_name}
                    </span>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 bg-gray-50 rounded-md">
          <p className="text-gray-500">No {visibility === 'all' ? '' : visibility === 'internal' ? 'internal' : 'client-visible'} notes are available for this project.</p>
        </div>
      )}
    </div>
  );
};

export default ProjectNotes; 